import React, { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AutoComplete from '@mui/material/Autocomplete';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import _ from 'lodash';

import { PaginationDirection } from 'services/url';
import { fetchSalesOrderById, SalesOrder } from 'services/salesOrders';
import { activeUserHasPermission } from 'services/user/redux';
import { Pagination } from 'services/search';
import { TextField } from 'ui/components/TextField/TextField';
import { initialPagination } from 'ui/components/Table/ItemsTable';

import { SalesOrderAsyncAutocompleteProps } from './types';
import {
  resolvedFetchSalesOrderFunction,
  salesOrderAutocompleteFilterOptions,
} from './helpers';

import { useAsyncAutocomplete, useScrollPosition } from '../Autocomplete/hooks';

const initialSalesOrderPagination: Pagination = {
  ...initialPagination,
  pageSize: 50,
  sort: { sortBy: 'dateCreated', direction: PaginationDirection.Ascending },
};

const SalesOrderAsyncAutocomplete: React.FC<
  SalesOrderAsyncAutocompleteProps
> = (props) => {
  const {
    onChange,
    dataQa,
    permissions = [],
    disabled,
    label,
    value,
    placeholder,
    error,
    required,
    additionalInputProps,
    expands,
    customQuickSearchColumns,
    helperText,
  } = props;

  const {
    data: salesOrder,
    isLoading,
    innerValue,
    open,
    onCloseAutocomplete,
    onNextPage,
    onOpenAutocomplete,
    setSearchValue,
    setInnerValue,
  } = useAsyncAutocomplete(
    value,
    resolvedFetchSalesOrderFunction(expands, customQuickSearchColumns),
    fetchSalesOrderById,
    initialSalesOrderPagination
  );
  useEffect(() => {
    onChange(innerValue);
  }, [innerValue]);

  const canEdit = useSelector(activeUserHasPermission(permissions));
  const { listRef, captureScrollPosition } = useScrollPosition(salesOrder);

  const isDisabled = disabled || !canEdit;

  const handleAutocompleteInputChange = (
    e: any,
    val: string,
    reason: string
  ) => {
    // reason why this event is triggered
    // it can be 'input', 'clear' and 'reset'
    if (reason === 'input') {
      if (!val) {
        onOpenAutocomplete();
        setInnerValue(null);
      }
      setSearchValue(val || null);
      onOpenAutocomplete();
      return;
    }

    if (reason === 'clear') {
      onChange(null);
    }

    setSearchValue(null);
  };

  const handleChange = (e: any, salesOrder: SalesOrder | null) => {
    if (salesOrder && (salesOrder.id === null || salesOrder.id < 0)) {
      return;
    }

    setInnerValue(salesOrder);
    onChange(salesOrder);
  };

  return (
    <>
      <AutoComplete
        className="redesign"
        // TODO : RESTYLING : minor style adjustment needed for now until we use the FBOAutoSelect component
        style={{ marginTop: '0px', marginBottom: '0px' }}
        options={salesOrder}
        open={open}
        disabled={isDisabled}
        value={innerValue}
        onOpen={onOpenAutocomplete}
        onClose={onCloseAutocomplete}
        onChange={handleChange}
        onInputChange={handleAutocompleteInputChange}
        getOptionLabel={(option: SalesOrder) => option.number || ''}
        filterOptions={salesOrderAutocompleteFilterOptions()}
        noOptionsText={isLoading ? 'Loading...' : 'No options'}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        selectOnFocus={false}
        ListboxProps={{
          onScroll: (event: React.SyntheticEvent) => {
            const node = event.currentTarget;
            if (
              Math.ceil(node.scrollTop + node.clientHeight) ===
              node.scrollHeight
            ) {
              onNextPage();
            }
          },
          // @ts-ignore
          ref: listRef,
          onScrollCapture: captureScrollPosition,
        }}
        renderInput={(params) => {
          const endAdornment = params.InputProps.endAdornment as any;

          return (
            <TextField
              // we are omitting props that would overwrite our styling in TextField
              {..._.omit(params, 'variant', 'size', 'InputLabelProps')}
              className="redesign"
              label={label}
              placeholder={placeholder}
              error={error}
              required={required}
              helperText={helperText}
              InputProps={{
                ...params.InputProps,
                ...additionalInputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {endAdornment}
                  </React.Fragment>
                ),
              }}
              inputProps={{
                'data-qa': dataQa,
                'data-lpignore': 'true',
                ...params.inputProps,
              }}
            />
          );
        }}
        clearIcon={
          dataQa && (
            <CloseIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-clear-icon`}
            />
          )
        }
        popupIcon={
          dataQa && (
            <ArrowDropDownIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-dropdown-icon`}
            />
          )
        }
      />
    </>
  );
};

export default memo(SalesOrderAsyncAutocomplete);
